import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import FieldRenderer from './FieldRenderer.jsx';
import { PencilSquareIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ArrowUpCircleIcon } from '@heroicons/react/24/solid';
import { startEditing, stopEditing } from '../../../../../redux/slices/isEditingSlice.js';
import {
  initializeDraft,
  updateDraftField,
  clearDraft
} from '../../../../../redux/slices/draftRecordSlice.js';
import {
  deleteMessageById,
  addSystemMessage
} from '../../../../../redux/slices/sessionSlice.js';
import { useSocket } from '../../../../../socket/socketContext.js';
import UpdatePreview from './UpdatePreview.jsx';
import { clearAllValidationErrors } from '../../../../../redux/slices/validationSlice.js';
import { v4 as uuidv4 } from 'uuid';
import AiAutofill from '../../../../dialogs/ai-autofill/AiAutofill.jsx';

import { DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';

const EMPTY_OBJ = Object.freeze({});

const selectDraftsByCompositeKey = (state) => state.draftRecord.draftsByCompositeKey;
const makeSelectDraftForCompositeKey = (compositeKey) =>
  createSelector([selectDraftsByCompositeKey], (drafts) => drafts[compositeKey] ?? EMPTY_OBJ);

export default function RecordCardFields({
  record,
  relatedRecords = [],
  showRelatedRecords,
  setShowRelatedRecords,
  messageId,
  variant
}) {
  const dispatch = useDispatch();
  const socket = useSocket();

  const [sending, setSending] = useState(false);

  // Track "copied" status per field
  const [copiedFields, setCopiedFields] = useState({});

  const chatObjects = useSelector((state) => state.chatObjects.chatObjects);
  const chatUserObjects = useSelector((state) => state.chatUserObjects.chatUserObjects);
  const onboarding = useSelector((state) => state.ui.onboarding);

  const isNoRecord = !record || !record.attributes?.type;
  const compositeKey = `${messageId}_${record?.Id}`;
  const editingRecords = useSelector((state) => state.isEditing.editingRecords);
  const isThisRecordBeingEdited = editingRecords[compositeKey]?.isEditing || false;

  // Draft record selector
  const draftSelector = useMemo(() => makeSelectDraftForCompositeKey(compositeKey), [compositeKey]);
  const draftRecord = useSelector(draftSelector);

  const validationState = useSelector(
    (state) => state.validation.validationsByCompositeKey[compositeKey]
  );
  const hasValidationErrors = !!validationState;

  // Debug
  useEffect(() => {
    console.log("Current draftRecord state:", draftRecord);
  }, [draftRecord]);

  useEffect(() => {
    if (isNoRecord) return;
    if (isThisRecordBeingEdited) {
      if (!draftRecord || Object.keys(draftRecord).length === 0) {
        const initialValues = {};
        Object.entries(record).forEach(([key, val]) => {
          if (key !== 'attributes' && key !== 'Id') {
            initialValues[key] = val ?? null;
          }
        });
        dispatch(initializeDraft({ recordId: record.Id, messageId, initialValues }));
      }
    } else {
      dispatch(clearDraft({ recordId: record.Id, messageId }));
    }
  }, [
    isNoRecord,
    isThisRecordBeingEdited,
    record,
    dispatch,
    messageId,
    draftRecord
  ]);

  const formatLastModified = (dateString) => {
    if (!dateString) return '';
    const lastModified = new Date(dateString);
    const now = new Date();
    const diffInMs = now - lastModified;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    let dateLabel = '';
    if (diffInDays === 0) {
      dateLabel = 'Today';
    } else if (diffInDays === 1) {
      dateLabel = 'Yesterday';
    } else {
      dateLabel = `${diffInDays} days ago`;
    }
    const time = lastModified.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return (
      <>
        {dateLabel} at <span className="font-semibold">{time}</span>
      </>
    );
  };

  const handleFieldChange = (fieldName, newValue, newDisplayValue) => {
    if (!record?.Id) return;
    dispatch(
      updateDraftField({
        recordId: record.Id,
        messageId,
        fieldName,
        newValue,
        displayName: newDisplayValue
      })
    );
  };

  const handleEditClick = () => {
    if (!record?.attributes) return;

    const editingType = variant === 'report' ? 'inlineReportUpdate' : 'update';
    dispatch(
      startEditing({
        recordId: record.Id,
        recordType: record.attributes.type,
        messageId,
        type: editingType
      })
    );
  };

  const handleCancelClick = () => {
    if (!record?.Id) return;
    const editingRecordEntry = editingRecords[compositeKey];
    if (editingRecordEntry && editingRecordEntry.type === 'create') {
      dispatch(deleteMessageById({ messageId }));
      socket.emit('cancelCreate', messageId);
    }
    dispatch(clearDraft({ recordId: record.Id, messageId }));
    dispatch(stopEditing({ recordId: record.Id, messageId }));
    dispatch(clearAllValidationErrors({ recordId: record.Id, messageId }));
  };

  const handleSaveClick = () => {
    if (!record?.Id) return;
    const updatedFields = [];

    for (const [fieldName, fieldData] of Object.entries(draftRecord)) {
      let newVal;
      if (fieldData && typeof fieldData === 'object' && 'value' in fieldData) {
        newVal = fieldData.value;
      } else {
        newVal = fieldData;
      }
      const originalVal = record[fieldName] ?? null;
      if (newVal !== originalVal) {
        updatedFields.push({ field: fieldName, oldValue: originalVal, newValue: newVal });
      }
    }

    const editingRecordEntry = editingRecords[compositeKey];
    const payloadType = editingRecordEntry ? editingRecordEntry.type : 'update';

    const submitFieldUpdates = {
      recordId: record.Id,
      recordType: record.attributes.type,
      updates: updatedFields,
      record,
      messageId,
      type: payloadType
    };

    socket.emit('submitUpdateCreate', submitFieldUpdates);
    setSending(true);

    setTimeout(() => {
      dispatch(clearDraft({ recordId: record.Id, messageId }));
      dispatch(stopEditing({ recordId: record.Id, messageId }));
      dispatch(clearAllValidationErrors({ recordId: record.Id, messageId }));
      setSending(false);
    }, 600);
  };

  if (isNoRecord) {
    return <div className="text-sm text-gray-500">No record data</div>;
  }

  // Identify the object & user config
  const objectType = record.attributes.type;
  const matchingChatObject = chatObjects.find((obj) => obj.name === objectType);
  const matchingChatUserObject = chatUserObjects.find((obj) => obj.name === objectType);

  // We expect "fields" in matchingChatObject. Also see if matchingChatUserObject has "fields" with aiAutofill
  const { fields = [] } = matchingChatObject;
  const userObjectFields = matchingChatUserObject?.fields || [];

  // Build a sorted layout
  const layout = matchingChatUserObject?.layout || matchingChatObject.layout || [];
  const sortedLayout = [...layout].sort((a, b) => a.position - b.position);
  const allFieldKeys = Object.keys(record).filter((k) => k !== 'attributes' && k !== 'Id');
  const layoutFieldNames = sortedLayout.map((item) => item.name);

  const layoutFields = sortedLayout
    .filter((layoutItem) => Object.prototype.hasOwnProperty.call(record, layoutItem.name))
    .map((layoutItem) => ({
      name: layoutItem.name,
      label: layoutItem.label,
      value: record[layoutItem.name] ?? null
    }));

  const leftoverKeys = allFieldKeys.filter((key) => !layoutFieldNames.includes(key));
  const leftoverFields = leftoverKeys.map((key) => {
    const matchingFieldDef = fields.find((f) => f.name === key);
    const label = matchingFieldDef?.label || key;
    return { name: key, label, value: record[key] ?? null };
  });

  const allDisplayFields = [...layoutFields, ...leftoverFields].filter(
    (field) => fields.some((metaField) => metaField.name === field.name)
  );

  // Copy logic
  const handleCopy = async (fieldName, value) => {
    try {
      await navigator.clipboard.writeText(String(value ?? ''));
      setCopiedFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => {
        setCopiedFields((prev) => ({ ...prev, [fieldName]: false }));
      }, 3000);
    } catch (err) {
      console.error('Failed to copy field value: ', err);
    }
  };

  const handleChangeLayoutClick = () => {
    if (!record?.attributes?.type) return;
    const layoutMessageId = uuidv4();
    dispatch(
      addSystemMessage({
        type: 'changeLayout',
        sender: 'system',
        content: { objectType: record.attributes.type },
        messageId: layoutMessageId,
        timestamp: Date.now()
      })
    );
  };

  // Remove the isFieldEmpty requirement so we always show AiAutofill
  // const isFieldEmpty = (fieldDefinition, rawCurrentValue, displayValue) => { ... };

  return (
    <div className="relative group text-xs sm:text-xsm md:text-xsm lg:text-xsm">
      <div className="grid grid-cols-2 gap-y-2 gap-x-4 iphone:gap-x-1 mb-3">

        {allDisplayFields.map((field) => {
          // Possibly the draft value for this field
          const draftVal = draftRecord[field.name];
          const safeDraftVal =
            draftVal && typeof draftVal === 'object'
              ? draftVal
              : { value: field.value, displayName: field.value || '' };

          const rawCurrentValue = safeDraftVal.value;
          const displayValue = rawCurrentVal =>
            rawCurrentValue == null ? '' : safeDraftVal.displayName;
          const rawOriginal = record[field.name] ?? null;
          const changed = rawCurrentValue !== rawOriginal;

          // Find the field metadata and user configuration
          const fieldDefinition = fields.find((fDef) => fDef.name === field.name);
          const userFieldDef = userObjectFields.find((fDef) => fDef.name === field.name);

          // Check if the current value is "empty":
          // We consider a value empty if it is null, undefined, or a string containing only spaces.
          const isFieldValueEmpty =
            rawCurrentValue === null ||
            rawCurrentValue === undefined ||
            (typeof rawCurrentValue === 'string' && rawCurrentValue.trim() === '');

          // Only show AiAutofill if:
          // 1. The field is configured with aiAutofill,
          // 2. The field value is empty,
          // 3. And the record is not currently being edited as type 'create'.
          const showAiAutofill =
            userFieldDef?.aiAutofill === true &&
            isFieldValueEmpty &&
            !(isThisRecordBeingEdited && editingRecords[compositeKey]?.type === 'create');

          // The copy icon should only show when the field has a non-empty value.
          const showCopyIcon = !isFieldValueEmpty;

          return (
            <div key={field.name} className="flex items-center w-full">
              <FieldRenderer
                fieldName={field.name}
                label={field.label}
                value={displayValue(rawCurrentValue)}
                rawValue={rawCurrentValue}
                fieldDefinition={fieldDefinition}
                relatedRecords={relatedRecords}
                showRelatedRecords={showRelatedRecords}
                setShowRelatedRecords={setShowRelatedRecords}
                isEditing={isThisRecordBeingEdited}
                onFieldChange={(fname, val, newDisplayValue) =>
                  handleFieldChange(fname, val, newDisplayValue)
                }
                isChanged={changed}
                recordId={record.Id}
                messageId={messageId}
              />

              <div className="inline-flex items-center ml-1 opacity-0 group-hover:opacity-100 transition-opacity flex-none">
                {showAiAutofill ? (
                  <AiAutofill
                    fieldName={field.name}
                    recordId={record.Id}
                    messageId={messageId}
                    variant="recordUpdate"
                  />
                ) : showCopyIcon ? (
                  <button
                    onClick={() => handleCopy(field.name, rawCurrentValue)}
                    aria-label="Copy field value"
                    className="inline-flex items-center ml-1 transition-opacity flex-none"
                  >
                    {copiedFields[field.name] ? (
                      <CheckIcon className="h-4 w-4 text-indigo-600" />
                    ) : (
                      <DocumentDuplicateIcon className="h-3 w-3 text-gray-300 hover:text-indigo-600" />
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between items-center mt-2 w-full">
        {record.LastModifiedDate ? (
          <span className="text-xxs text-gray-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
            <strong>Last Modified: </strong>
            {formatLastModified(record.LastModifiedDate)}
          </span>
        ) : (
          <span />
        )}
        <div className="flex items-center space-x-2">
          {isThisRecordBeingEdited && !onboarding && (
            <span
              className="text-xxs lg:text-xs mt-2 mr-4 text-indigo-600 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={handleChangeLayoutClick}
            >
              Add / remove fields
            </span>
          )}
          {!isThisRecordBeingEdited ? (
            <PencilSquareIcon
              onClick={handleEditClick}
              className="h-5 w-5 md:h-5 md:w-5 lg:h-6 lg:w-6 text-indigo-600 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
            />
          ) : (
            <>
              <XCircleIcon
                onClick={handleCancelClick}
                className="h-8 w-8 md:h-9 md:w-9 lg:h-9 lg:w-9 text-red-300 cursor-pointer hover:text-red-500 transition-colors"
                title="Cancel"
              />
              <ArrowUpCircleIcon
                onClick={!hasValidationErrors && !onboarding ? handleSaveClick : undefined}
                className={`h-8 w-8 md:h-9 md:w-9 lg:h-9 lg:w-9 transition-colors ${hasValidationErrors || onboarding
                  ? 'text-gray-400 pointer-events-none'
                  : 'text-indigo-500 cursor-pointer hover:text-indigo-700'
                  }`}
                title="Send Updates"
              />
            </>
          )}
        </div>
      </div>

      {isThisRecordBeingEdited && (
        <div>
          <UpdatePreview
            record={record}
            draftRecord={draftRecord}
            sending={sending}
            messageId={messageId}
          />
        </div>
      )}
    </div>
  );
}